// Client-side portrait prep, shared by the Oracle scan and Glowup uploads.
//
//   file ─▶ decode (<img> + img.decode(): native EXIF orientation)
//        ─▶ getLandmarkerWithTimeout(4s)      null ──▶ fallback (passthrough)
//        ─▶ landmarker.detect(img)            no face ─▶ fallback
//        ─▶ eye centers ─▶ similarity transform (deroll/scale/translate)
//        ─▶ canvas OUT_W×OUT_H: black fill, matrix drawImage, toBlob(jpeg)
//        ─▶ { imageBase64, mimeType, previewUrl, landmarks, meshEdges,
//             normalized: true, alignedCanvas }   (alignedCanvas → Stage 4 model)
//
// Every failure on the normalize path (model missing, no face, no canvas in
// jsdom, decode error) falls back to passing the original file through
// untouched — the backend Rekognition screen remains the rejection authority.
import {
  OUT_W,
  OUT_H,
  JPEG_QUALITY,
  MESH_EDGE_STRIDE,
} from './constants';
import {
  eyeCentersPx,
  computeNormalizeTransform,
  landmarksToOutputFractions,
  strideEdges,
} from './geometry';
import { warmFaceLandmarker, getLandmarkerWithTimeout } from './landmarker';

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = String(result).indexOf(',');
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error || new Error('Read failed.'));
    reader.readAsDataURL(file);
  });
}

const canCreateObjectURL = () =>
  typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

// Fire-and-forget model warm-up for upload-page mounts. Never throws.
export function warmFacePrep() {
  try {
    warmFaceLandmarker();
  } catch (err) {
    /* warm-up is best-effort */
  }
}

async function decodeImage(file) {
  // <img> decode applies EXIF orientation natively (Safari ≥13.1), avoiding
  // createImageBitmap's historically broken imageOrientation option.
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    if (!img.naturalWidth || !img.naturalHeight) {
      throw new Error('Decode produced empty image.');
    }
    return { img, release: () => URL.revokeObjectURL(url) };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

async function drawNormalized(img, t, srcW, srcH) {
  const canvas = document.createElement('canvas');
  canvas.width = OUT_W;
  canvas.height = OUT_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D canvas.'); // jsdom → fallback
  // Out-of-frame regions fill black — invisible against the Obsidian UI.
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, OUT_W, OUT_H);
  // Same similarity transform as transformPoint(), as canvas operations
  // (applied in reverse order to the image pixels).
  ctx.translate(t.target.x, t.target.y);
  ctx.rotate(-t.theta);
  ctx.scale(t.s, t.s);
  ctx.translate(-t.mid.x, -t.mid.y);
  ctx.drawImage(img, 0, 0, srcW, srcH);
  // Return the canvas alongside the blob: the learned-score model
  // (src/lib/aestheticModel) reads aligned pixels straight from the canvas,
  // BEFORE JPEG encode, so it doesn't inherit recompression drift.
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null.'))),
      'image/jpeg',
      JPEG_QUALITY
    );
  });
  return { blob, canvas };
}

async function passthrough(file) {
  return {
    imageBase64: await fileToBase64(file),
    mimeType: file.type,
    previewUrl: canCreateObjectURL() ? URL.createObjectURL(file) : '',
    landmarks: null,
    meshEdges: null,
    normalized: false,
    // No verified alignment → the learned-score model must not run (its
    // accuracy depends on the facePrep alignment).
    alignedCanvas: null,
  };
}

export async function preparePortrait(file) {
  try {
    const loaded = await getLandmarkerWithTimeout();
    if (!loaded) return await passthrough(file);

    const { img, release } = await decodeImage(file);
    try {
      const result = loaded.landmarker.detect(img);
      const faceLandmarks =
        result && result.faceLandmarks && result.faceLandmarks[0];
      if (!faceLandmarks || !faceLandmarks.length) {
        return await passthrough(file);
      }

      const srcW = img.naturalWidth;
      const srcH = img.naturalHeight;
      const { left, right } = eyeCentersPx(faceLandmarks, srcW, srcH);
      const t = computeNormalizeTransform(left, right, srcW, srcH);
      if (!t) return await passthrough(file);

      const { blob, canvas } = await drawNormalized(img, t, srcW, srcH);
      return {
        imageBase64: await fileToBase64(blob),
        mimeType: 'image/jpeg',
        previewUrl: canCreateObjectURL() ? URL.createObjectURL(blob) : '',
        landmarks: landmarksToOutputFractions(faceLandmarks, t, srcW, srcH),
        meshEdges: strideEdges(loaded.tessellation || [], MESH_EDGE_STRIDE),
        normalized: true,
        alignedCanvas: canvas,
      };
    } finally {
      release();
    }
  } catch (err) {
    return passthrough(file);
  }
}
